/**
 * Component to display jobs for a stream with auto-refresh
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  InfoOutlined as InfoOutlinedIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobApi, type Job } from '@/api/job';
import { StreamJobDetailsModal } from './StreamJobDetailsModal';

interface StreamJobsListProps {
  streamUuid: string;
}


export const StreamJobsList: React.FC<StreamJobsListProps> = ({ streamUuid }) => {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showChildImporterJobs, setShowChildImporterJobs] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Track if we've set the initial default value
  const hasSetInitialDefault = useRef(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isWithdrawDetailsOpen, setIsWithdrawDetailsOpen] = useState(false);
  const [jobPendingRetry, setJobPendingRetry] = useState<Job | null>(null);

  const {
    data: jobsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['jobs', streamUuid, showChildImporterJobs],
    queryFn: ({ pageParam }) =>
      jobApi.getStreamJobs(streamUuid, {
        page_token: pageParam,
        max_results: 20,
        include_sub_jobs: showChildImporterJobs,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined as string | undefined,
    refetchInterval: autoRefresh ? 5000 : false, // Refresh every 5 seconds when enabled
  });

  const updateJobMutation = useMutation({
    mutationFn: ({ jobUuid, data }: { jobUuid: string; data: Parameters<typeof jobApi.updateJob>[1] }) =>
      jobApi.updateJob(jobUuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', streamUuid] });
    },
  });

  // Set default auto-refresh state only once when jobs data is first loaded
  useEffect(() => {
    const jobs = jobsData?.pages.flatMap((page) => page.output.items) || [];
    if (jobs.length > 0 && !hasSetInitialDefault.current) {
      const hasRunningJobs = jobs.some(
        (job) => job.status === 'IN_PROGRESS' || job.status === 'TODO'
      );
      if (hasRunningJobs) {
        setAutoRefresh(true);
      }
      hasSetInitialDefault.current = true;
    }
  }, [jobsData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleStop = (jobUuid: string) => {
    updateJobMutation.mutate({ jobUuid, data: { status: 'STOP' } });
  };

  const handleResume = (jobUuid: string) => {
    updateJobMutation.mutate({ jobUuid, data: { status: 'TODO' } });
  };

  const retryFailedUrlsMutation = useMutation({
    mutationFn: (jobUuid: string) => jobApi.retryFailedUrls(jobUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', streamUuid] });
    },
  });

  const hasFailedUrls = (job: Job): boolean => {
    const processedUrls = job.payload?.processed_urls || {};
    return Object.values(processedUrls).some((status) => status !== 'success');
  };

  const handleRetryFailedUrls = (jobUuid: string) => {
    retryFailedUrlsMutation.mutate(jobUuid);
  };

  const confirmRetryFailedJob = () => {
    if (!jobPendingRetry) return;
    const job = jobPendingRetry;
    const isWithdrawJob = job.queue === 'withdraw_pending_invites';
    const useFailedUrlsRetry = !isWithdrawJob && hasFailedUrls(job);
    setJobPendingRetry(null);
    if (useFailedUrlsRetry) {
      handleRetryFailedUrls(job.key);
    } else {
      updateJobMutation.mutate({ jobUuid: job.key, data: { status: 'TODO' } });
    }
  };

  const getFailedJobRetryDialogBody = (job: Job): string => {
    const isWithdrawJob = job.queue === 'withdraw_pending_invites';
    if (!isWithdrawJob && hasFailedUrls(job)) {
      return 'Are you sure you want to retry this job? Failed URL results will be cleared and those URLs will be processed again.';
    }
    return 'Are you sure you want to retry this job? It will be marked as scheduled so it can run again.';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getJobStats = (job: Job) => {
    const urls = job.payload?.urls || [];
    const processedUrls = job.payload?.processed_urls || {};
    const total = urls.length;
    const success = Object.values(processedUrls).filter((status) => status === 'success').length;
    const error = Object.values(processedUrls).filter((status) => status === 'error' || status.startsWith('error')).length;
    const notFound = Object.values(processedUrls).filter((status) => status === 'not_found').length;
    const processing = Object.values(processedUrls).filter((status) => status === 'processing').length;

    return { total, success, error, notFound, processing };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'FAILED':
        return 'error';
      case 'STOP':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleOpenDetails = (job: Job) => {
    setSelectedJob(job);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedJob(null);
  };

  const handleOpenWithdrawDetails = (job: Job) => {
    setSelectedJob(job);
    setIsWithdrawDetailsOpen(true);
  };

  const handleCloseWithdrawDetails = () => {
    setIsWithdrawDetailsOpen(false);
    setSelectedJob(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load jobs. Please try again.</Alert>
      </Box>
    );
  }

  const jobs = jobsData?.pages.flatMap((page) => page.output.items) || [];
  const visibleJobs = jobs;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Jobs</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Show per-URL child jobs created by the importer (can be very noisy).">
            <FormControlLabel
              control={
                <Switch
                  checked={showChildImporterJobs}
                  onChange={(e) => setShowChildImporterJobs(e.target.checked)}
                />
              }
              label="Show importer sub-jobs"
            />
          </Tooltip>
          <FormControlLabel
            control={
              <Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            }
            label="Auto-refresh"
          />
        </Box>
      </Box>

      {visibleJobs.length === 0 ? (
        <Alert severity="info">No jobs found for this stream.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {visibleJobs.map((job) => {
            const isWithdrawJob = job.queue === 'withdraw_pending_invites';
            const stats = getJobStats(job);
            const progressValue = job.progress !== null && job.progress !== undefined ? job.progress : 0;
            const progress = isWithdrawJob
              ? (progressValue > 1 ? Math.round(progressValue) : Math.round(progressValue * 100))
              : Math.round(progressValue * 100);

            return (
              <Card key={job.key}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {job.queue}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip label={job.status} color={getStatusColor(job.status) as any} size="small" />
                        {job.started_at && (
                          <Chip
                            icon={<ScheduleIcon />}
                            label={`Started: ${formatDate(job.started_at)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {job.available_at && !job.started_at && (
                          <Chip
                            icon={<ScheduleIcon />}
                            label={`Scheduled: ${formatDate(job.available_at)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {isWithdrawJob && job.created_at && (
                          <Chip
                            label={`Created: ${formatDate(job.created_at)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Stop button - show for IN_PROGRESS or TODO jobs */}
                      {(job.status === 'IN_PROGRESS' || job.status === 'TODO') && (
                        <Tooltip title="Stop job">
                          <IconButton
                            size="small"
                            onClick={() => handleStop(job.key)}
                            disabled={updateJobMutation.isPending}
                            color="error"
                          >
                            <StopIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Resume button - show for STOP jobs */}
                      {job.status === 'STOP' && (
                        <Tooltip title="Resume job">
                          <IconButton
                            size="small"
                            onClick={() => handleResume(job.key)}
                            disabled={updateJobMutation.isPending}
                            color="primary"
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Retry failed URLs — non-FAILED jobs only (instant); FAILED uses confirmation dialog below */}
                      {!isWithdrawJob && hasFailedUrls(job) && job.status !== 'FAILED' && (
                        <Tooltip title="Retry failed URLs">
                          <IconButton
                            size="small"
                            onClick={() => handleRetryFailedUrls(job.key)}
                            disabled={updateJobMutation.isPending || retryFailedUrlsMutation.isPending}
                            color="warning"
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {job.status === 'FAILED' && (
                        <Tooltip title="Retry job">
                          <IconButton
                            size="small"
                            onClick={() => setJobPendingRetry(job)}
                            disabled={updateJobMutation.isPending || retryFailedUrlsMutation.isPending}
                            color="warning"
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="View job details">
                        <IconButton
                          size="small"
                          onClick={() => (isWithdrawJob ? handleOpenWithdrawDetails(job) : handleOpenDetails(job))}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {job.status === 'IN_PROGRESS' && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Progress</Typography>
                        <Typography variant="body2">{progress}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                  )}

                  {!isWithdrawJob && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total URLs
                        </Typography>
                        <Typography variant="h6">{stats.total}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Success
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {stats.success}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Errors
                        </Typography>
                        <Typography variant="h6" color="error.main">
                          {stats.error}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Not Found
                        </Typography>
                        <Typography variant="h6" color="warning.main">
                          {stats.notFound}
                        </Typography>
                      </Box>
                      {stats.processing > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Processing
                          </Typography>
                          <Typography variant="h6" color="info.main">
                            {stats.processing}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {isWithdrawJob && job.failed_reason && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {job.failed_reason}
                    </Alert>
                  )}

                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
      <div ref={sentinelRef} style={{ height: 24 }}>
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </div>

      <Dialog
        open={jobPendingRetry !== null}
        onClose={() => setJobPendingRetry(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Retry job</DialogTitle>
        <DialogContent dividers>
          {jobPendingRetry && (
            <Typography variant="body1">{getFailedJobRetryDialogBody(jobPendingRetry)}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobPendingRetry(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ReplayIcon />}
            onClick={confirmRetryFailedJob}
            disabled={updateJobMutation.isPending || retryFailedUrlsMutation.isPending}
          >
            Retry job
          </Button>
        </DialogActions>
      </Dialog>

      <StreamJobDetailsModal
        open={isDetailsOpen}
        onClose={handleCloseDetails}
        job={selectedJob}
      />

      <Dialog open={isWithdrawDetailsOpen} onClose={handleCloseWithdrawDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Job Details</Typography>
            <IconButton onClick={handleCloseWithdrawDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedJob && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip label={selectedJob.status} color={getStatusColor(selectedJob.status) as any} size="small" />
              </Box>
              {selectedJob.progress !== null && selectedJob.progress !== undefined && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={selectedJob.progress > 1 ? selectedJob.progress : selectedJob.progress * 100}
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography variant="body2">
                      {Math.round(selectedJob.progress > 1 ? selectedJob.progress : selectedJob.progress * 100)}%
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">{formatDate(selectedJob.created_at)}</Typography>
              </Box>
              {selectedJob.started_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJob.started_at)}</Typography>
                </Box>
              )}
              {selectedJob.available_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Scheduled
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJob.available_at)}</Typography>
                </Box>
              )}
              {selectedJob.failed_reason && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Error
                  </Typography>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {selectedJob.failed_reason}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWithdrawDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

